:root {
  color-scheme: light;
  --bg: #eef4f6;
  --surface: #ffffff;
  --surface-soft: #f6f9fb;
  --ink: #111827;
  --muted: #6b7280;
  --line: #dbe3ea;
  --teal: #087f8f;
  --teal-dark: #075f6b;
  --teal-soft: #e3f6f8;
  --indigo: #3e56c4;
  --green: #1f8a5b;
  --amber: #b76b00;
  --red: #b42318;
  --shadow: 0 24px 70px rgba(17, 24, 39, 0.14);
  --shadow-soft: 0 12px 36px rgba(17, 24, 39, 0.08);
  --ease: cubic-bezier(0.2, 0.8, 0.2, 1);
}

* {
  box-sizing: border-box;
}

body {
  margin: 0;
  min-width: 320px;
  background:
    radial-gradient(circle at top left, rgba(8, 127, 143, 0.13), transparent 34vw),
    linear-gradient(135deg, #eef4f6 0%, #f8fafc 44%, #edf4f8 100%);
  color: var(--ink);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  -webkit-font-smoothing: antialiased;
}

#root {
  min-height: 100vh;
}

button,
input {
  font: inherit;
}

button {
  cursor: pointer;
  transition:
    transform 180ms var(--ease),
    box-shadow 180ms var(--ease),
    background 180ms var(--ease),
    border-color 180ms var(--ease),
    color 180ms var(--ease);
}

h1,
h2,
h3,
p {
  margin-top: 0;
}

.eyebrow {
  margin-bottom: 8px;
  color: var(--teal-dark);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.login-page {
  min-height: 100vh;
  display: grid;
  grid-template-columns: minmax(0, 1fr) clamp(500px, 31vw, 620px);
  background-color: #f7fbff;
}

.login-artwork {
  display: grid;
  min-width: 0;
  min-height: 100vh;
  place-items: center;
  overflow: hidden;
  background: #f7fbff;
}

.login-artwork img {
  display: block;
  width: 100%;
  height: 100vh;
  object-fit: contain;
}

.login-credentials {
  display: grid;
  min-height: 100vh;
  padding: clamp(32px, 4vw, 64px);
  place-items: center;
  border-left: 1px solid rgba(180, 204, 224, 0.7);
  background:
    radial-gradient(circle at 50% 42%, rgba(215, 236, 249, 0.66), transparent 48%),
    #f7fbff;
}

.login-art {
  position: relative;
  display: flex;
  align-items: flex-end;
  overflow: hidden;
  padding: clamp(32px, 6vw, 72px);
  background:
    linear-gradient(135deg, rgba(13, 31, 42, 0.28), rgba(5, 56, 65, 0.82)),
    url("https://images.unsplash.com/photo-1579154204601-01588f351e67?auto=format&fit=crop&w=1800&q=84") center/cover;
  color: #fff;
}

.scan-lines {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(255, 255, 255, 0.08) 1px, transparent 1px),
    linear-gradient(rgba(255, 255, 255, 0.07) 1px, transparent 1px);
  background-size: 44px 44px;
  mask-image: linear-gradient(to bottom, transparent, black 18%, black 82%, transparent);
  animation: driftGrid 14s linear infinite;
}

.login-art::after {
  position: absolute;
  width: 42vw;
  height: 42vw;
  right: -14vw;
  top: -14vw;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.16), transparent 56%);
  content: "";
  animation: breathe 7s ease-in-out infinite;
}

.hero-copy {
  position: relative;
  max-width: 650px;
  animation: riseIn 620ms var(--ease) both;
}

.hero-copy h1 {
  margin-bottom: 14px;
  font-size: clamp(42px, 7vw, 84px);
  line-height: 0.98;
}

.hero-copy p {
  max-width: 560px;
  color: rgba(255, 255, 255, 0.86);
  font-size: 18px;
  line-height: 1.6;
}

.status-pill,
.user-pill,
.storage-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 999px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 13px;
  font-weight: 750;
}

.login-panel {
  width: min(470px, 100%);
  padding: 42px 44px 44px;
  border: 1px solid rgba(180, 204, 224, 0.82);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 24px 70px rgba(38, 82, 116, 0.2);
  backdrop-filter: blur(12px);
  animation: floatIn 520ms var(--ease) both;
}

.brand-mark,
.stat-icon {
  display: grid;
  width: 44px;
  height: 44px;
  place-items: center;
  border-radius: 12px;
  background: #fff;
  color: #fff;
  box-shadow: 0 12px 30px rgba(8, 31, 43, 0.14);
}

.brand-mark img {
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: contain;
}

.compact-mark {
  width: 48px;
  height: 48px;
  flex: 0 0 auto;
  padding: 2px;
}

.login-panel h2 {
  margin-bottom: 28px;
  font-size: clamp(30px, 2vw, 38px);
  white-space: nowrap;
}

.login-panel .auth-form {
  gap: 20px;
}

.login-panel .auth-form label {
  gap: 9px;
  font-size: 16px;
}

.login-panel .auth-form input {
  min-height: 58px;
  padding: 14px 16px;
  font-size: 17px;
}

.login-panel .primary-button {
  min-height: 56px;
  margin-top: 2px;
  font-size: 17px;
}

.auth-form,
.upload-form,
.add-info-form,
.logout-form {
  display: grid;
  gap: 16px;
}

.field,
.auth-form label {
  display: grid;
  gap: 8px;
  color: #334155;
  font-size: 14px;
  font-weight: 700;
}

.upload-field {
  min-width: 0;
}

.field input,
.auth-form input,
.search-box input {
  width: 100%;
  min-height: 44px;
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 10px 12px;
  background: #fff;
  color: var(--ink);
}

.field input:focus,
.auth-form input:focus,
.search-box input:focus {
  border-color: var(--teal);
  box-shadow: 0 0 0 3px rgba(13, 124, 138, 0.14);
  outline: none;
}

.upload-card {
  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 12px;
  min-height: 72px;
  align-items: center;
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 14px;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(246, 251, 252, 0.96)),
    var(--surface);
  box-shadow: 0 10px 24px rgba(17, 24, 39, 0.05);
  overflow: hidden;
  transition:
    transform 180ms var(--ease),
    border-color 180ms var(--ease),
    box-shadow 180ms var(--ease),
    background 180ms var(--ease);
}

.upload-card::after {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, rgba(8, 127, 143, 0.08), transparent);
  content: "";
  transform: translateX(-100%);
  transition: transform 520ms var(--ease);
}

.upload-card:hover,
.upload-card:focus-within {
  border-color: rgba(8, 127, 143, 0.42);
  box-shadow: 0 16px 34px rgba(8, 127, 143, 0.14);
  transform: translateY(-2px);
}

.upload-card:hover::after,
.upload-card:focus-within::after {
  transform: translateX(100%);
}

.upload-card-icon {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 12px;
  background: var(--teal-soft);
  color: var(--teal-dark);
}

.upload-card-copy {
  display: grid;
  min-width: 0;
  gap: 3px;
}

.upload-card-copy strong {
  color: var(--ink);
  font-size: 14px;
}

.upload-card-copy span {
  overflow: hidden;
  color: var(--muted);
  font-size: 13px;
  font-weight: 650;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.upload-card input {
  position: absolute;
  inset: 0;
  width: 100%;
  min-height: 100%;
  opacity: 0;
  cursor: pointer;
}

.ct-unified-field {
  position: relative;
}

.ct-upload-card {
  width: 100%;
  border: 1px solid var(--line);
  text-align: left;
}

.format-popover {
  position: absolute;
  z-index: 20;
  top: calc(100% + 10px);
  left: 0;
  width: min(360px, 100%);
  display: grid;
  gap: 8px;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: var(--shadow);
  animation: floatIn 180ms var(--ease) both;
}

.format-popover.is-hidden {
  display: none;
}

.format-option {
  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 10px;
  align-items: center;
  border: 1px solid transparent;
  border-radius: 12px;
  padding: 11px;
  color: var(--ink);
  cursor: pointer;
}

.format-option:hover,
.format-option:focus-within {
  border-color: rgba(8, 127, 143, 0.2);
  background: var(--teal-soft);
}

.format-option svg {
  color: var(--teal-dark);
}

.format-option span {
  display: grid;
  gap: 2px;
}

.format-option strong {
  font-size: 14px;
}

.format-option small {
  color: var(--muted);
  font-size: 12px;
  font-weight: 700;
}

.format-option input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.primary-button,
.secondary-button,
.ghost-button,
.text-button,
.icon-button,
.side-nav button,
.file-button {
  display: inline-flex;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 800;
  text-decoration: none;
}

.primary-button {
  border: 0;
  background: linear-gradient(135deg, var(--teal), #0aa0a8);
  color: #fff;
  box-shadow: 0 10px 24px rgba(8, 127, 143, 0.22);
}

.primary-button:hover {
  background: linear-gradient(135deg, var(--teal-dark), #087f8f);
  box-shadow: 0 14px 30px rgba(8, 127, 143, 0.28);
  transform: translateY(-1px);
}

.primary-button:disabled {
  background: #9ca3af;
  box-shadow: none;
  cursor: not-allowed;
  transform: none;
}

.secondary-button,
.ghost-button,
.icon-button {
  border: 1px solid var(--line);
  background: #fff;
  color: var(--ink);
}

.secondary-button:hover,
.ghost-button:hover,
.icon-button:hover {
  background: #eef3f8;
  transform: translateY(-1px);
}

.text-button {
  min-height: 32px;
  border: 0;
  padding: 0;
  background: transparent;
  color: var(--teal-dark);
}

.alert {
  border-radius: 12px;
  padding: 12px 14px;
  font-weight: 700;
  animation: slideDown 260ms var(--ease) both;
}

.alert.error {
  border: 1px solid #ffd2cc;
  background: #fff0ee;
  color: var(--red);
}

.alert.success {
  border: 1px solid #c9ebd2;
  background: #eaf7ee;
  color: #137333;
}

.app-shell {
  min-height: 100dvh;
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
}

.sidebar {
  position: sticky;
  top: 0;
  height: 100dvh;
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 24px;
  border-right: 1px solid var(--line);
  padding: 24px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(247, 251, 252, 0.94)),
    radial-gradient(circle at top, rgba(8, 127, 143, 0.12), transparent 280px);
  backdrop-filter: blur(14px);
}

.product-mark {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-mark svg {
  color: var(--teal-dark);
}

.product-mark strong,
.product-mark span {
  display: block;
}

.product-mark span {
  color: var(--muted);
  font-size: 12px;
  font-weight: 750;
}

.side-nav {
  display: grid;
  align-content: start;
  gap: 8px;
}

.side-nav button {
  justify-content: flex-start;
  border: 1px solid transparent;
  background: transparent;
  color: #475467;
  position: relative;
}

.side-nav button.active,
.side-nav button:hover {
  border-color: var(--line);
  background: #fff;
  color: var(--teal-dark);
  box-shadow: var(--shadow-soft);
  transform: translateX(2px);
}

.side-nav button.active::before {
  position: absolute;
  left: -1px;
  top: 10px;
  bottom: 10px;
  width: 3px;
  border-radius: 999px;
  background: var(--teal);
  content: "";
}

.workspace {
  display: grid;
  align-content: start;
  gap: 22px;
  min-width: 0;
  padding: clamp(18px, 3vw, 28px) clamp(16px, 3vw, 36px) 36px;
  animation: riseIn 420ms var(--ease) both;
}

.workspace-head,
.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.workspace-head h1 {
  margin-bottom: 0;
  font-size: 34px;
  letter-spacing: -0.02em;
}

.user-pill {
  border-color: var(--line);
  background: #fff;
  color: var(--ink);
}

.view-stack {
  display: grid;
  gap: 22px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(160px, 1fr));
  gap: 16px;
}

.stat-card,
.surface-panel {
  border: 1px solid var(--line);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: var(--shadow-soft);
  backdrop-filter: blur(14px);
}

.stat-card {
  position: relative;
  display: grid;
  gap: 12px;
  padding: 18px;
  overflow: hidden;
  transition:
    transform 180ms var(--ease),
    box-shadow 180ms var(--ease),
    border-color 180ms var(--ease);
}

.stat-card::after {
  position: absolute;
  inset: auto 0 0;
  height: 3px;
  background: linear-gradient(90deg, transparent, rgba(8, 127, 143, 0.7), transparent);
  content: "";
  transform: translateX(-100%);
  transition: transform 500ms var(--ease);
}

.stat-card:hover {
  border-color: rgba(8, 127, 143, 0.25);
  box-shadow: 0 20px 44px rgba(17, 24, 39, 0.12);
  transform: translateY(-3px);
}

.stat-card:hover::after {
  transform: translateX(100%);
}

.stat-card span {
  color: var(--muted);
  font-size: 13px;
  font-weight: 750;
}

.stat-card strong {
  font-size: 34px;
  line-height: 1;
}

.stat-spark {
  position: absolute;
  right: 14px;
  bottom: 14px;
  width: 74px;
  height: 28px;
  opacity: 0.42;
  background:
    linear-gradient(135deg, transparent 10%, rgba(8, 127, 143, 0.25) 10% 20%, transparent 20% 36%, rgba(8, 127, 143, 0.32) 36% 48%, transparent 48% 66%, rgba(8, 127, 143, 0.28) 66% 76%, transparent 76%),
    linear-gradient(to top, rgba(8, 127, 143, 0.12), transparent);
  border-radius: 8px;
}

.stat-card.indigo .stat-icon {
  background: #edf0ff;
  color: var(--indigo);
}

.stat-card.green .stat-icon {
  background: #eaf7ee;
  color: var(--green);
}

.stat-card.amber .stat-icon {
  background: #fff5df;
  color: var(--amber);
}

.surface-panel {
  padding: 22px;
  animation: panelIn 360ms var(--ease) both;
}

.activity-list {
  display: grid;
  margin-top: 12px;
}

.activity-row {
  display: grid;
  grid-template-columns: auto minmax(220px, 1fr) auto auto;
  gap: 16px;
  align-items: center;
  border-top: 1px solid var(--line);
  padding: 14px 0;
  transition: background 160ms var(--ease), transform 160ms var(--ease);
}

.activity-row:first-child {
  border-top: 0;
}

.activity-row:hover {
  background: rgba(8, 127, 143, 0.035);
  transform: translateX(3px);
}

.activity-row strong,
.activity-row span,
.activity-row time {
  display: block;
}

.activity-row span,
.activity-row time {
  color: var(--muted);
  font-size: 13px;
  font-weight: 650;
}

.record-badge,
.type-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: var(--teal-soft);
  color: var(--teal-dark);
  font-size: 12px;
  font-weight: 850;
}

.record-badge {
  width: 38px;
  height: 38px;
}

.type-chip {
  min-height: 28px;
  padding: 5px 10px;
}

.import-status-panel .section-head {
  align-items: flex-start;
}

.import-status-table table {
  min-width: 980px;
}

.import-status-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

.import-status-summary span {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  border: 1px solid var(--line);
  border-radius: 999px;
  padding: 5px 10px;
  background: var(--surface-soft);
  color: var(--muted);
  font-size: 12px;
  font-weight: 850;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  border-radius: 999px;
  padding: 5px 10px;
  background: var(--surface-soft);
  color: #475467;
  font-size: 12px;
  font-weight: 850;
  text-transform: capitalize;
}

.status-chip.processing {
  background: var(--teal-soft);
  color: var(--teal-dark);
}

.status-chip.imported {
  background: #eaf7ee;
  color: var(--green);
}

.status-chip.failed,
.status-chip.missing,
.status-chip.not_configured {
  background: #fff0ee;
  color: var(--red);
}

.status-chip.waiting,
.status-chip.queued {
  background: #fff5df;
  color: var(--amber);
}

.status-message {
  min-width: 220px;
  color: var(--muted);
}

.upload-panel {
  max-width: 980px;
}

.upload-form {
  grid-template-columns: repeat(2, minmax(240px, 1fr));
  margin-top: 18px;
}

.add-info-form {
  grid-template-columns: repeat(2, minmax(240px, 1fr)) auto;
  align-items: end;
  margin-top: 18px;
}

.add-info-grid {
  display: grid;
  gap: 18px;
  margin-top: 18px;
}

.add-info-grid .add-info-form {
  margin-top: 0;
}

.ct-add-form {
  grid-template-columns: minmax(260px, 1fr) auto;
  border-top: 1px solid var(--line);
  padding-top: 18px;
}

.segmented-files {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.file-button {
  position: relative;
  border: 1px solid var(--line);
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(246, 251, 252, 0.96)),
    #fff;
  color: var(--teal-dark);
  overflow: hidden;
  box-shadow: 0 8px 18px rgba(17, 24, 39, 0.05);
}

.file-button:hover {
  border-color: rgba(8, 127, 143, 0.28);
  background: #eef8fa;
  transform: translateY(-1px);
}

.file-button input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.file-selection,
.muted {
  color: var(--muted);
  font-size: 13px;
  font-weight: 650;
}

.file-selection {
  display: inline-flex;
  min-height: 28px;
  align-items: center;
  border: 1px dashed rgba(8, 127, 143, 0.24);
  border-radius: 999px;
  padding: 5px 10px;
  background: rgba(227, 246, 248, 0.45);
  width: fit-content;
  max-width: 100%;
}

.submit-upload {
  justify-self: start;
}

.studies-head {
  align-items: flex-end;
}

.search-box {
  display: flex;
  width: min(360px, 100%);
  align-items: center;
  gap: 8px;
  border: 1px solid var(--line);
  border-radius: 999px;
  padding: 0 12px;
  background: #fff;
  color: var(--muted);
  box-shadow: 0 8px 24px rgba(17, 24, 39, 0.05);
  transition: border-color 180ms var(--ease), box-shadow 180ms var(--ease);
}

.search-box:focus-within {
  border-color: rgba(8, 127, 143, 0.42);
  box-shadow: 0 0 0 4px rgba(8, 127, 143, 0.1);
}

.search-box input {
  border: 0;
  padding-left: 0;
  box-shadow: none;
}

.search-box input:focus {
  box-shadow: none;
}

.table-shell {
  margin-top: 18px;
  overflow-x: auto;
}

table {
  width: 100%;
  min-width: 1120px;
  border-collapse: collapse;
}

th,
td {
  border-top: 1px solid var(--line);
  padding: 13px 10px;
  text-align: left;
  vertical-align: middle;
}

th {
  color: #667085;
  font-size: 12px;
  font-weight: 850;
  letter-spacing: 0;
  text-transform: uppercase;
}

td {
  color: #334155;
  font-size: 14px;
}

tbody tr {
  transition: background 150ms var(--ease);
}

tbody tr:hover {
  background: rgba(8, 127, 143, 0.035);
}

.mono {
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
  font-size: 13px;
}

.filename-cell {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filename-cell .subtle-line {
  color: var(--muted);
  display: block;
  font-size: 0.82rem;
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reveal-cell {
  display: grid;
  gap: 4px;
}

.report-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.report-list a {
  color: var(--teal-dark);
  font-weight: 800;
  text-decoration: none;
}

.report-list a:hover {
  text-decoration: underline;
}

.storage-chip {
  border-color: var(--line);
  background: var(--surface-soft);
  color: #475467;
}

.compact {
  min-height: 36px;
  padding: 8px 10px;
}

.profile-grid {
  display: grid;
  gap: 18px;
  min-width: 0;
}

.profile-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  min-width: 0;
  overflow: hidden;
}

.profile-hero h2 {
  margin-bottom: 0;
  font-size: 26px;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.profile-hero > div {
  min-width: 0;
}

.back-button {
  flex: 0 0 auto;
}

.profile-meta {
  display: flex;
  flex: 0 1 auto;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  min-width: 0;
}

.detail-panel h3,
.reports-panel h3 {
  margin-bottom: 0;
}

.detail-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 18px 0 0;
  min-width: 0;
}

.detail-list div {
  min-width: 0;
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 14px;
  background: var(--surface-soft);
}

.detail-list dt {
  margin-bottom: 8px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 850;
  text-transform: uppercase;
}

.detail-list dd {
  margin: 0;
  color: var(--ink);
  font-weight: 750;
  overflow-wrap: anywhere;
}

.report-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 18px;
}

.report-tabs button {
  display: inline-flex;
  min-height: 38px;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 8px 12px;
  background: #fff;
  color: #475467;
  font-weight: 800;
}

.report-tabs button.active,
.report-tabs button:hover {
  border-color: rgba(13, 124, 138, 0.3);
  background: #e7f6f7;
  color: var(--teal-dark);
  transform: translateY(-1px);
}

.report-frame {
  margin-top: 14px;
  border: 1px solid var(--line);
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
}

.report-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid var(--line);
  padding: 12px 14px;
  background: var(--surface-soft);
}

.report-toolbar strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.report-toolbar span {
  color: var(--muted);
  font-size: 12px;
  font-weight: 850;
}

.report-preview {
  width: 100%;
  min-height: min(620px, 62dvh);
  border: 0;
  background: #fff;
}

.document-preview {
  padding: 28px;
  overflow: auto;
  color: #1f2937;
  line-height: 1.65;
}

.text-preview {
  margin: 0;
  padding: 22px;
  overflow: auto;
  color: #1f2937;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
  font-size: 13px;
  white-space: pre-wrap;
}

.image-preview {
  display: grid;
  min-height: 460px;
  place-items: center;
  padding: 18px;
}

.image-preview img {
  max-width: 100%;
  max-height: 760px;
  object-fit: contain;
}

.loading-preview,
.unsupported-preview {
  display: grid;
  min-height: 320px;
  place-items: center;
  gap: 8px;
  color: var(--muted);
  font-weight: 750;
  text-align: center;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: grid;
  place-items: start center;
  overflow-y: auto;
  padding: clamp(12px, 4dvh, 28px) 16px;
  background: rgba(15, 23, 42, 0.48);
}

.modal {
  width: min(460px, 100%);
  max-height: calc(100dvh - 32px);
  display: grid;
  gap: 14px;
  border-radius: 8px;
  padding: 20px;
  background: #fff;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.26);
  overflow-y: auto;
  overscroll-behavior: contain;
}

.download-modal {
  width: min(540px, 100%);
}

.viewer-modal {
  width: min(1180px, 100%);
  max-height: calc(100dvh - 32px);
  gap: 12px;
}

.modal-head,
.modal-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.modal-head h3 {
  margin-bottom: 0;
  font-size: 18px;
}

.modal-actions {
  justify-content: flex-end;
}

.icon-button {
  width: 38px;
  padding: 0;
}

.icon-button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
  transform: none;
}

.compact-table table {
  min-width: 760px;
}

.viewer-tabs,
.slice-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.viewer-tabs {
  flex-wrap: wrap;
}

.viewer-tabs button {
  display: inline-flex;
  min-height: 38px;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 8px 12px;
  background: #fff;
  color: #475467;
  font-weight: 800;
}

.viewer-tabs button.active,
.viewer-tabs button:hover:not(:disabled) {
  border-color: rgba(13, 124, 138, 0.3);
  background: #e7f6f7;
  color: var(--teal-dark);
}

.viewer-tabs button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.dicom-viewer,
.viewer-report {
  display: grid;
  min-height: 0;
  gap: 10px;
}

.viewer-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 10px 12px;
  background: var(--surface-soft);
}

.viewer-toolbar strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.viewer-toolbar span {
  color: var(--muted);
  font-size: 12px;
  font-weight: 850;
}

.dicom-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 360px);
  gap: 12px;
  min-height: 0;
}

.dicom-stage {
  display: grid;
  min-height: min(520px, 46dvh);
  place-items: center;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #111827;
  overflow: hidden;
}

.dicom-stage img {
  display: block;
  width: 100%;
  height: min(520px, 46dvh);
  object-fit: contain;
}

.metadata-panel {
  display: grid;
  align-content: start;
  min-height: 0;
  max-height: min(520px, 46dvh);
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}

.metadata-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-bottom: 1px solid var(--line);
  padding: 10px 12px;
  background: var(--surface-soft);
}

.metadata-head span,
.metadata-row small {
  color: var(--muted);
  font-size: 12px;
  font-weight: 800;
}

.metadata-table {
  overflow: auto;
}

.metadata-row {
  display: grid;
  gap: 4px;
  border-bottom: 1px solid var(--line);
  padding: 10px 12px;
}

.metadata-row span {
  color: var(--muted);
  font-size: 12px;
  font-weight: 850;
}

.metadata-row strong {
  color: var(--ink);
  font-size: 13px;
  font-weight: 750;
  overflow-wrap: anywhere;
}

.metadata-state {
  padding: 18px 12px;
  color: var(--muted);
  font-weight: 750;
  text-align: center;
}

.slice-controls {
  justify-content: center;
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 8px;
  background: #fff;
}

.slice-controls input {
  width: min(520px, 100%);
  accent-color: var(--teal);
}

.check-row {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #334155;
  font-weight: 750;
}

.check-row input {
  width: 18px;
  height: 18px;
  accent-color: var(--teal);
}

.download-quick-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.download-option {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 12px;
  background: var(--surface-soft);
  color: var(--ink);
  cursor: pointer;
  transition:
    border-color 160ms var(--ease),
    background 160ms var(--ease),
    transform 160ms var(--ease);
}

.download-option:hover,
.download-option:focus-within {
  border-color: rgba(8, 127, 143, 0.3);
  background: var(--teal-soft);
  transform: translateY(-1px);
}

.download-option input {
  width: 18px;
  height: 18px;
  accent-color: var(--teal);
}

.download-option span {
  display: grid;
  min-width: 0;
  gap: 3px;
}

.download-option strong {
  font-size: 14px;
}

.download-option small {
  overflow: hidden;
  color: var(--muted);
  font-size: 12px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.empty-state,
.loading-screen {
  display: grid;
  min-height: 180px;
  place-items: center;
  gap: 8px;
  color: var(--muted);
  font-weight: 750;
  text-align: center;
}

.app-error {
  min-height: 100dvh;
  padding: 24px;
}

.app-error strong {
  color: var(--ink);
  font-size: 20px;
}

.app-error span {
  max-width: 560px;
  color: var(--muted);
  line-height: 1.5;
}

.loading-screen::before {
  width: 34px;
  height: 34px;
  border: 3px solid rgba(8, 127, 143, 0.15);
  border-top-color: var(--teal);
  border-radius: 50%;
  content: "";
  animation: spin 850ms linear infinite;
}

@keyframes riseIn {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes floatIn {
  from {
    opacity: 0;
    transform: translateY(18px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes panelIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes driftGrid {
  from {
    background-position: 0 0, 0 0;
  }
  to {
    background-position: 44px 0, 0 44px;
  }
}

@keyframes breathe {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.65;
  }
  50% {
    transform: scale(1.08);
    opacity: 0.95;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 1ms !important;
    scroll-behavior: auto !important;
    transition-duration: 1ms !important;
  }
}

@media (max-width: 1120px) {
  .login-page {
    grid-template-columns: 1fr;
  }

  .login-artwork {
    min-height: auto;
  }

  .login-artwork img {
    width: 100%;
    height: auto;
  }

  .login-credentials {
    min-height: auto;
    padding: 28px 20px 40px;
    border-top: 1px solid rgba(180, 204, 224, 0.7);
    border-left: 0;
  }

  .app-shell {
    grid-template-columns: 1fr;
  }

  .sidebar {
    position: static;
    height: auto;
    grid-template-columns: 1fr;
    border-right: 0;
    border-bottom: 1px solid var(--line);
  }

  .side-nav {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .stats-grid {
    grid-template-columns: repeat(2, minmax(160px, 1fr));
  }
}

@media (max-width: 680px) {
  .login-credentials {
    padding: 20px 14px 28px;
  }

  .login-panel {
    width: min(100%, 440px);
    padding: 24px;
  }

  .login-panel h2 {
    font-size: clamp(27px, 8vw, 34px);
    white-space: normal;
  }

  .workspace {
    padding: 14px 10px 28px;
  }

  .surface-panel {
    padding: 16px;
  }

  .modal-backdrop {
    padding: 10px;
  }

  .modal {
    max-height: calc(100dvh - 20px);
    padding: 16px;
  }

  .workspace-head,
  .section-head,
  .studies-head {
    align-items: stretch;
    flex-direction: column;
  }

  .side-nav,
  .stats-grid,
  .upload-form,
  .add-info-form,
  .activity-row,
  .profile-hero,
  .detail-list {
    grid-template-columns: 1fr;
  }

  .profile-hero {
    align-items: stretch;
    flex-direction: column;
  }

  .profile-meta {
    justify-content: flex-start;
  }

  .submit-upload,
  .primary-button,
  .secondary-button {
    width: 100%;
  }

  .dicom-stage,
  .dicom-stage img {
    min-height: 360px;
    height: 360px;
  }

  .dicom-layout {
    grid-template-columns: 1fr;
  }

  .metadata-panel {
    max-height: 320px;
  }

  .slice-controls {
    display: grid;
    grid-template-columns: 38px minmax(0, 1fr) 38px;
  }
}

/* Multi-hospital clinical platform */
.platform-shell { min-height: 100vh; display: grid; grid-template-columns: 252px minmax(0,1fr); background: #f4f7f9; color: #172331; }
.platform-sidebar { position: sticky; top: 0; height: 100vh; display: flex; flex-direction: column; padding: 22px 14px 16px; border-right: 1px solid #dce4e9; background: #0e2931; color: #dbe9ec; }
.platform-brand { display: flex; align-items: center; gap: 11px; padding: 0 10px 22px; border-bottom: 1px solid rgba(255,255,255,.1); }
.platform-brand img { width: 38px; height: 38px; border-radius: 8px; background: white; }
.platform-brand div,.tenant-context div { display: grid; gap: 2px; }.platform-brand strong{font-size:17px;color:#fff}.platform-brand span,.tenant-context span{font-size:11px;color:#91aab0;text-transform:uppercase;letter-spacing:.08em}
.tenant-context { display:flex;align-items:center;gap:10px;margin:18px 6px;padding:12px;border:1px solid rgba(255,255,255,.1);background:rgba(255,255,255,.04); }.tenant-context strong{font-size:13px;color:#fff}
.platform-sidebar nav { display:grid;gap:3px;overflow:auto }.platform-sidebar nav button,.platform-sidebar form button{display:flex;width:100%;align-items:center;gap:11px;border:0;border-radius:6px;padding:10px 12px;background:transparent;color:#afc2c7;font:inherit;font-size:13px;font-weight:650;text-align:left;cursor:pointer}.platform-sidebar nav button:hover,.platform-sidebar nav button.active{background:#17434d;color:#fff}.platform-sidebar nav button.active{box-shadow:inset 3px 0 #25a7b7}.platform-sidebar form{margin-top:auto;padding-top:14px;border-top:1px solid rgba(255,255,255,.1)}
.platform-main{min-width:0}.platform-topbar{height:58px;display:flex;align-items:center;justify-content:space-between;padding:0 30px;border-bottom:1px solid #dce4e9;background:#fff;color:#53636f;font-size:12px}.platform-topbar div{display:flex;align-items:center;gap:7px;color:#176373;font-weight:700}.platform-topbar span{font-weight:700;color:#263743}.platform-content{max-width:1580px;margin:0 auto;padding:28px 30px 56px}
.platform-page-header{display:flex;align-items:flex-end;justify-content:space-between;gap:24px;margin-bottom:24px}.platform-page-header>div{max-width:780px}.platform-page-header span,.section-heading span{display:block;margin-bottom:5px;color:#287588;font-size:11px;font-weight:800;letter-spacing:.11em;text-transform:uppercase}.platform-page-header h1{margin:0;color:#172331;font-size:30px;line-height:1.16;letter-spacing:-.025em}.platform-page-header p{margin:7px 0 0;color:#63727d;font-size:14px;line-height:1.55}
.platform-metrics{display:grid;grid-template-columns:repeat(4,minmax(150px,1fr));gap:12px;margin-bottom:22px}.platform-metrics article{display:grid;grid-template-columns:auto 1fr;gap:4px 10px;padding:18px;border:1px solid #dbe4e9;background:#fff}.platform-metrics svg{grid-row:1/3;color:#258092}.platform-metrics span{color:#6a7882;font-size:12px}.platform-metrics strong{font-size:24px;color:#182a36}
.platform-section{border:1px solid #dbe4e9;background:#fff;padding:20px}.section-heading{display:flex;justify-content:space-between;align-items:center;margin-bottom:16px}.section-heading h2{margin:0;font-size:18px}.platform-table-wrap{overflow:auto;border:1px solid #dbe4e9;background:#fff}.platform-table{width:100%;border-collapse:separate;border-spacing:0;font-size:13px}.platform-table th{position:sticky;top:0;z-index:1;padding:11px 13px;border-bottom:1px solid #cfd9df;background:#edf2f5;color:#53636e;font-size:10px;letter-spacing:.07em;text-align:left;text-transform:uppercase}.platform-table td{padding:12px 13px;border-bottom:1px solid #e5ebef;vertical-align:middle}.platform-table tr:last-child td{border-bottom:0}.platform-table tbody tr:hover{background:#f8fbfc}.platform-table td strong{display:block;color:#163544}.platform-table td small{display:block;max-width:260px;margin-top:3px;overflow:hidden;color:#7b8991;font-size:11px;text-overflow:ellipsis;white-space:nowrap}.icon-action{display:grid;width:30px;height:30px;place-items:center;border:1px solid #d3dee4;background:white;color:#286f7e;cursor:pointer}
.platform-badge{display:inline-flex!important;width:max-content;align-items:center;border:1px solid #cfd9df;border-radius:999px;padding:4px 8px;color:#52636e!important;background:#f3f6f8;font-size:10px!important;font-weight:800;letter-spacing:.04em;text-transform:uppercase}.status-ready,.status-completed,.status-success,.status-active{border-color:#b8dfd1;background:#edf9f4;color:#187355!important}.status-failed,.status-denied,.status-deleted{border-color:#efc5c5;background:#fff1f1;color:#a43c3c!important}.status-processing,.status-anonymizing,.status-uploading,.status-queued{border-color:#b9dcec;background:#eef8fc;color:#176b8b!important}.status-review_required,.status-unknown{border-color:#ecd9aa;background:#fff9e9;color:#8a6612!important}
.platform-toolbar{display:flex;align-items:center;justify-content:space-between;gap:20px;margin-bottom:12px;color:#667681;font-size:12px}.platform-search{display:flex;width:min(520px,100%);align-items:center;gap:9px;border:1px solid #cfd9df;background:white;padding:0 12px}.platform-search input{width:100%;height:42px;border:0;outline:0;font:inherit}.platform-empty{padding:34px!important;color:#7a8992!important;text-align:center}.platform-loading,.platform-error,.platform-success{display:flex;align-items:center;gap:9px;padding:15px 17px;border:1px solid #d8e3e8;background:#fff;color:#5b6a74;font-size:13px}.platform-error{border-color:#efcaca;background:#fff5f5;color:#9a3d3d}.platform-success{margin-bottom:16px;border-color:#bce3d5;background:#effaf6;color:#176a50}.spin{animation:spin .8s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}
.text-action{margin-bottom:15px;border:0;background:transparent;color:#237286;font-weight:750;cursor:pointer}.profile-grid{display:grid;grid-template-columns:minmax(0,1.35fr) minmax(320px,.65fr);gap:16px}.resource-list{display:grid;gap:8px}.resource-list article{display:grid;grid-template-columns:auto minmax(0,1fr) auto auto;align-items:center;gap:12px;padding:13px;border:1px solid #e0e7eb}.resource-list article>div:nth-child(2){display:grid;gap:3px}.resource-list article span,.resource-list article small{color:#71808a;font-size:11px}.resource-icon{display:grid;width:38px;height:38px;place-items:center;background:#eaf4f6;color:#24788a}.resource-icon svg{width:18px}.secondary-button,.primary-button{display:inline-flex;align-items:center;justify-content:center;gap:7px;border:1px solid #bfcfd7;padding:9px 12px;background:#fff;color:#245f6d;font:inherit;font-size:12px;font-weight:750;text-decoration:none;cursor:pointer}.primary-button{border-color:#138796;background:#138f9e;color:#fff}.primary-button:disabled{opacity:.55;cursor:not-allowed}
.upload-workflow{display:grid;gap:18px;max-width:980px}.form-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px}.form-grid label{display:grid;gap:7px;color:#4d5e68;font-size:12px;font-weight:750}.form-grid select{height:44px;border:1px solid #cbd7de;background:#fff;padding:0 11px}.file-drop{position:relative;display:grid;min-height:170px;place-items:center;align-content:center;gap:8px;border:1px dashed #9dbbc5;background:#f7fbfc;color:#3f6470;text-align:center;cursor:pointer}.file-drop input{position:absolute;inset:0;opacity:0;cursor:pointer}.file-drop span{color:#75858e;font-size:12px}.upload-overlay{position:fixed;inset:0;z-index:1000;display:grid;place-items:center;background:rgba(8,27,34,.76);backdrop-filter:blur(5px)}.upload-overlay>div{display:grid;width:min(500px,calc(100% - 32px));gap:13px;padding:34px;border:1px solid #bdd5dc;background:#fff;text-align:center}.upload-overlay svg{margin:auto;color:#168595}.upload-overlay h2,.upload-overlay p{margin:0}.upload-overlay progress{width:100%;height:12px}.upload-overlay small{color:#71808a}
.hospital-grid{display:grid;grid-template-columns:repeat(3,minmax(220px,1fr));gap:12px}.hospital-grid article{padding:20px;border:1px solid #dbe4e9;background:#fff}.hospital-grid article>svg{color:#24798b}.hospital-grid h2{margin:13px 0 3px;font-size:17px}.hospital-grid>article>span{color:#71808a;font-size:11px}.hospital-grid article div{display:flex;gap:6px;margin-top:15px}.hospital-grid b{border:1px solid #b9d9e1;background:#edf8fa;padding:5px 8px;color:#236879;font-size:10px}
.header-actions,.modal-actions{display:flex;align-items:center;gap:10px}.platform-modal-backdrop{position:fixed;inset:0;z-index:1200;display:grid;place-items:center;padding:24px;background:rgba(7,25,38,.55)}.platform-modal{width:min(520px,100%);max-height:85vh;overflow:auto;border:1px solid #cddde6;border-radius:12px;padding:24px;background:#fff;box-shadow:0 24px 70px rgba(4,28,43,.3)}.download-choice{display:flex;align-items:center;gap:12px;padding:13px 4px;border-bottom:1px solid #e5edf2}.download-choice input{width:17px;height:17px}.modal-actions{justify-content:flex-end;margin-top:22px}.form-grid input{height:44px;border:1px solid #cbd7de;background:#fff;padding:0 11px;font:inherit}
.program-toggle{border:1px solid #c8d6dd;background:#fff;padding:5px 8px;color:#65757e;font-size:10px;font-weight:800;cursor:pointer}.program-toggle.active{border-color:#75bdc8;background:#eaf8fa;color:#176979}
.resource-actions{display:flex!important;gap:6px!important}.secondary-button.danger{border-color:#e3baba;color:#9b3939}
.fatal-recovery{min-height:100vh;display:grid;place-items:center;padding:24px;background:#f3f7f9;color:#172331}.fatal-recovery section{width:min(520px,100%);border:1px solid #ccdbe2;background:#fff;padding:30px;box-shadow:0 16px 50px rgba(11,42,55,.12)}.fatal-recovery h1{margin:0 0 10px;font-size:24px}.fatal-recovery p,.fatal-recovery small{display:block;color:#64747d}.fatal-recovery button{margin:18px 0;border:0;background:#138f9e;color:#fff;padding:11px 16px;font-weight:750;cursor:pointer}.fatal-recovery code{display:block;overflow-wrap:anywhere;border-left:3px solid #d49838;padding:9px 12px;background:#fff8e8;color:#754f13;font-size:12px}.fatal-recovery pre{max-height:130px;overflow:auto;white-space:pre-wrap;color:#61717a;font-size:10px}
@media(max-width:1120px){.platform-shell{grid-template-columns:210px minmax(0,1fr)}.platform-sidebar{padding-inline:8px}.platform-content{padding-inline:20px}.platform-metrics{grid-template-columns:repeat(2,minmax(150px,1fr))}.profile-grid{grid-template-columns:1fr}.hospital-grid{grid-template-columns:repeat(2,minmax(220px,1fr))}}
@media(max-width:760px){.platform-shell{display:block}.platform-sidebar{position:static;height:auto}.platform-sidebar nav{grid-template-columns:repeat(2,1fr)}.platform-sidebar form{margin-top:10px}.platform-topbar{padding-inline:15px}.platform-content{padding:20px 12px 40px}.platform-page-header{align-items:flex-start;flex-direction:column}.platform-metrics,.form-grid,.hospital-grid{grid-template-columns:1fr}.resource-list article{grid-template-columns:auto 1fr}.resource-list article>.platform-badge,.resource-list article>.secondary-button{grid-column:2}.platform-table{min-width:860px}}

/* Hospital workspace visual system */
.platform-shell{grid-template-columns:252px minmax(0,1fr);background:#f8faff;color:#0c2047}
.platform-sidebar{padding:0 13px 24px;border-right:1px solid #e1e7f1;background:#fff;color:#3f5378}
.platform-brand{height:94px;gap:9px;padding:7px 13px;border-bottom:1px solid #e8edf5}
.platform-brand img{width:132px;height:78px;border-radius:0;background:#fff;object-fit:contain;object-position:left center}
.platform-brand i{position:relative;width:27px;height:27px;background:linear-gradient(90deg,transparent 34%,#f58220 34% 66%,transparent 66%)}
.platform-brand i:after{position:absolute;inset:0;content:"";background:linear-gradient(transparent 34%,#64748b 34% 66%,transparent 66%)}
.platform-brand strong{color:#10234d;font-size:26px;font-weight:750;letter-spacing:-1.5px}
.tenant-context{display:none}
.platform-sidebar nav{gap:9px;margin-top:16px;overflow:visible}
.platform-sidebar nav button,.platform-sidebar form button{border-radius:10px;padding:16px 14px;color:#465a7f;font-size:14px;font-weight:500}
.platform-sidebar nav button:hover,.platform-sidebar nav button.active{background:#edf3ff;color:#064bdd}
.platform-sidebar nav button.active{box-shadow:none}
.platform-sidebar form{margin-top:auto;padding-top:10px;border-top:0}
.platform-sidebar form button:hover{background:#f7f9fc;color:#0b4dd9}
.platform-topbar{height:94px;padding:0 34px;border-bottom:1px solid #e1e7f1;background:#fff}
.platform-topbar>img{width:510px;height:82px;object-fit:cover;object-position:center 45%;mix-blend-mode:multiply}
.account-summary{display:flex!important;align-items:center!important;gap:12px!important;color:#10234d!important}
.account-summary>b{display:grid;width:42px;height:42px;place-items:center;border-radius:50%;background:#e5f7ea;color:#15863b;font-size:13px}
.account-summary span{display:grid;gap:3px;color:#10234d!important}.account-summary strong{font-size:13px}.account-summary small{color:#657493;font-size:11px;font-weight:500}
.platform-content{max-width:none;padding:32px 31px 48px}
.hospital-dashboard .platform-page-header{align-items:center;margin-bottom:24px}.hospital-dashboard .platform-page-header>div{max-width:none}
.hospital-dashboard .platform-page-header>div>span{display:none}.hospital-dashboard .platform-page-header h1{color:#10234d;font-size:31px}.hospital-dashboard .platform-page-header p{color:#657493;font-size:15px}
.dashboard-utilities{gap:5px}.dashboard-utilities button{border:0;background:transparent;color:#5a6d91;font-size:11px;cursor:pointer}.dashboard-utilities button:hover{color:#064bdd}
.hospital-dashboard .platform-metrics{grid-template-columns:repeat(4,minmax(190px,1fr));gap:24px;margin-bottom:27px}
.hospital-dashboard .platform-metrics article{position:relative;display:grid;grid-template-columns:60px 1fr;grid-template-rows:auto auto 1fr;gap:5px 15px;min-height:160px;padding:23px 17px;border:1px solid #dfe6f1;border-radius:12px;box-shadow:0 2px 7px rgba(25,52,93,.04)}
.hospital-dashboard .metric-icon{grid-row:1/4;display:grid;width:58px;height:58px;place-items:center;border-radius:50%;background:#e8efff;color:#0751df}
.hospital-dashboard .metric-2 .metric-icon{background:#e6f8ec;color:#13a445}.hospital-dashboard .metric-3 .metric-icon{background:#f0e9ff;color:#8750d8}.hospital-dashboard .metric-4 .metric-icon{background:#fff1dc;color:#f57b0b}
.hospital-dashboard .platform-metrics span{align-self:end;color:#334a73;font-size:13px}.hospital-dashboard .platform-metrics strong{color:#10234d;font-size:30px;font-weight:650}
.hospital-dashboard .platform-metrics article button{display:flex;align-items:center;gap:3px;align-self:end;width:max-content;border:0;background:transparent;color:#004eea;font-size:12px;cursor:pointer}
.dashboard-filterbar{display:grid;grid-template-columns:minmax(300px,1fr) 210px auto;align-items:end;gap:28px;margin-bottom:28px}
.dashboard-filterbar .platform-search{width:auto;height:54px;border-color:#dfe6f1;border-radius:10px;color:#63769a}.dashboard-filterbar .platform-search input{height:52px;color:#10234d}
.dashboard-filterbar>label:not(.platform-search){display:grid;gap:6px;color:#536789;font-size:11px}.dashboard-filterbar select{height:45px;border:1px solid #dfe6f1;border-radius:7px;padding:0 12px;background:#fff;color:#172b52}
.dashboard-filterbar .primary-button{height:46px;border-radius:7px;padding-inline:25px;background:#0754d8}
.dashboard-patient-list{padding:0;border-color:#dfe6f1;border-radius:11px;overflow:hidden}.dashboard-patient-list .platform-table-wrap{border:0}
.platform-table th{padding:15px 14px;background:#fbfcff;color:#22365d;font-size:10px;font-weight:650;letter-spacing:0;text-transform:none}.platform-table td{padding:16px 14px;color:#193058}.platform-table td strong{color:#10234d}.platform-table tbody tr:hover{background:#f8faff}.icon-action{border:0;color:#21477e}
.primary-button{border-radius:7px;border-color:#0754d8;background:#0754d8}.secondary-button{border-radius:7px}
.platform-badge{border:0;border-radius:5px;padding:5px 10px;letter-spacing:0;text-transform:capitalize}.status-ready,.status-completed,.status-success,.status-active{background:#e3f6e9;color:#128038!important}.status-processing,.status-anonymizing,.status-uploading,.status-queued{background:#e7efff;color:#0754d8!important}
.resource-list article{grid-template-columns:48px minmax(0,1fr) auto;grid-template-areas:"icon details status" "icon actions actions";align-items:center;column-gap:14px;row-gap:10px}
.resource-list article>.resource-icon{grid-area:icon}.resource-list article>div:nth-child(2){grid-area:details;min-width:0}.resource-list article>.platform-badge{grid-area:status;align-self:start}.resource-list article>.resource-actions{grid-area:actions;display:flex!important;flex-wrap:wrap;justify-content:flex-end;min-width:0}
.resource-list article>div:nth-child(2) strong,.resource-list article>div:nth-child(2) span,.resource-list article>div:nth-child(2) small{max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.resource-list .secondary-button{min-width:74px;white-space:nowrap}
@media(max-width:1200px){.platform-topbar>img{width:390px}.hospital-dashboard .platform-metrics{grid-template-columns:repeat(2,minmax(190px,1fr))}}
@media(max-width:760px){.platform-shell{display:block}.platform-sidebar{position:static;height:auto;padding-bottom:12px}.platform-brand{height:70px}.platform-sidebar nav{grid-template-columns:repeat(3,1fr)}.platform-sidebar form{margin-top:4px}.platform-topbar{height:auto;min-height:72px;padding:8px 14px}.platform-topbar>img{display:none}.platform-content{padding:22px 12px 40px}.hospital-dashboard .platform-metrics{grid-template-columns:1fr}.dashboard-filterbar{grid-template-columns:1fr;gap:12px}.dashboard-utilities{flex-wrap:wrap}.platform-table{min-width:850px}.resource-list article{grid-template-columns:42px minmax(0,1fr);grid-template-areas:"icon details" "status status" "actions actions"}.resource-list article>.platform-badge{justify-self:start}.resource-list article>.resource-actions{justify-content:flex-start}}

/* Study Portal login */
.login-page{position:relative;grid-template-columns:minmax(520px,1fr) minmax(590px,.96fr);min-height:100vh;padding-bottom:58px;overflow:hidden;background:radial-gradient(circle at 18% 15%,#eaf3ff 0,transparent 31%),linear-gradient(135deg,#f5f9ff,#fff 58%,#f2f7ff)}
.login-artwork{position:relative;min-height:calc(100vh - 58px);place-items:center;background:transparent;overflow:visible}
.login-artwork:before,.login-artwork:after{position:absolute;width:54px;height:54px;content:"";opacity:.27;background:linear-gradient(90deg,transparent 34%,#dbe9fb 34% 66%,transparent 66%),linear-gradient(transparent 34%,#dbe9fb 34% 66%,transparent 66%)}
.login-artwork:before{top:64px;left:24%}.login-artwork:after{top:255px;right:3%}
.login-introduction{position:relative;z-index:1;width:min(610px,calc(100% - 70px));padding:42px 0}
.login-artwork img.login-brand-logo{display:block;width:170px;height:150px;margin:0 0 16px -8px;object-fit:contain;object-position:left center;mix-blend-mode:multiply}
.login-introduction h1{margin:0 0 8px;color:#0d214f;font-size:45px;line-height:1.08;letter-spacing:-1.5px}.login-introduction h2{margin:0 0 18px;color:#58698e;font-size:19px;font-weight:500}.login-introduction>p{max-width:590px;margin:0;color:#56688d;font-size:16px;line-height:1.65}
.institution-grid{display:grid;grid-template-columns:repeat(3,minmax(140px,1fr));gap:13px;margin-top:38px}
.institution-card{display:grid;height:138px;place-items:center;overflow:hidden;border:1px solid #e4eaf4;border-radius:13px;padding:12px;background:rgba(255,255,255,.94);box-shadow:0 9px 28px rgba(28,59,111,.08)}
.login-artwork .institution-card img{display:block;width:auto;height:auto;max-width:100%;max-height:110px;object-fit:contain}

/* Integrated patient imaging and clinical-file workspace */
.profile-grid{grid-template-columns:1fr;gap:18px}.profile-grid>.platform-section{min-width:0}
.add-data-dialog{width:min(680px,100%)}.upload-type-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}.upload-type-grid button{display:grid;min-height:120px;place-items:center;align-content:center;gap:10px;border:1px solid #d8e2ef;border-radius:10px;background:#f9fbff;color:#17325e;cursor:pointer}.upload-type-grid button:hover{border-color:#0754d8;background:#edf4ff;color:#0754d8}.upload-type-grid svg{width:25px;height:25px}.upload-type-grid strong{font-size:13px}
.clinical-workspace{position:fixed;inset:0;z-index:1500;display:grid;grid-template-rows:58px minmax(0,1fr);background:#07101f;color:#fff}.clinical-workspace>header{display:flex;align-items:center;justify-content:space-between;padding:0 18px;border-bottom:1px solid #263956;background:#071126}.clinical-workspace>header>div{display:grid;gap:3px}.clinical-workspace>header strong{font-size:14px}.clinical-workspace>header span{color:#9db2d2;font-size:11px}.clinical-workspace>header button{display:flex;align-items:center;gap:7px;border:1px solid #3a4d69;border-radius:6px;padding:8px 12px;background:#101d31;color:#fff;cursor:pointer}.clinical-workspace-body{display:grid;grid-template-columns:minmax(0,58%) minmax(380px,42%);min-height:0}.clinical-workspace-body.preview-only{grid-template-columns:1fr;background:#eef3f9}.dicom-frame,.document-frame{width:100%;height:100%;border:0}.dicom-frame{background:#000}.clinical-files-pane{display:grid;min-width:0;min-height:0;grid-template-rows:auto minmax(0,1fr);border-left:1px solid #cbd6e4;background:#f3f6fa;color:#172b4d}.clinical-file-tabs{display:flex;gap:7px;overflow-x:auto;padding:10px;border-bottom:1px solid #d5deea;background:#fff}.clinical-file-tabs button{display:grid;grid-template-columns:auto auto;gap:2px 7px;min-width:150px;border:1px solid #d5deea;border-radius:6px;padding:8px 10px;background:#f8faff;color:#405474;text-align:left;cursor:pointer}.clinical-file-tabs button.active{border-color:#1262d8;background:#eaf2ff;color:#0754d8}.clinical-file-tabs svg{grid-row:1/3;width:17px}.clinical-file-tabs strong{font-size:11px}.clinical-file-tabs small{max-width:120px;overflow:hidden;font-size:9px;text-overflow:ellipsis;white-space:nowrap}.document-frame{background:#fff}.preview-empty{display:grid;place-items:center;padding:30px;color:#6b7d98;text-align:center}
.platform-modal .form-grid{min-width:0}.platform-modal .form-grid label{min-width:0;overflow:hidden}.platform-modal .form-grid input,.platform-modal .form-grid select{box-sizing:border-box;width:100%;min-width:0;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.imaging-file-drop{cursor:default}.file-source-actions{position:relative;z-index:2;display:flex;flex-wrap:wrap;justify-content:center;gap:10px;margin-top:10px}.file-source-actions label{position:relative;overflow:hidden;cursor:pointer}.imaging-file-drop .file-source-actions input{position:absolute;inset:0;width:100%;height:100%;opacity:0;cursor:pointer}
@media(max-width:620px){.platform-modal .form-grid{grid-template-columns:1fr}}
@media(max-width:900px){.upload-type-grid{grid-template-columns:repeat(2,1fr)}.clinical-workspace-body{grid-template-columns:1fr;grid-template-rows:minmax(45%,1fr) minmax(45%,1fr)}.clinical-files-pane{border-top:1px solid #cbd6e4;border-left:0}}
.login-credentials{min-height:calc(100vh - 58px);padding:30px clamp(32px,4vw,70px);border:0;background:transparent}
.login-panel{display:flex;width:min(650px,100%);min-height:700px;flex-direction:column;justify-content:center;padding:64px 72px;border:1px solid rgba(225,232,243,.75);border-radius:25px;background:rgba(255,255,255,.93);box-shadow:0 18px 65px rgba(35,69,124,.13);backdrop-filter:blur(12px)}
.login-heading{text-align:center}.login-panel .login-heading h2{margin:0;color:#0c214f;font-size:35px;white-space:normal}.login-heading p{margin:11px 0 44px;color:#596b90;font-size:17px}
.login-panel .auth-form{gap:27px}.login-panel .auth-form label{gap:10px;color:#1b315e;font-size:15px;font-weight:500}
.login-input{display:flex;min-height:70px;align-items:center;gap:15px;border:1px solid #cad5e6;border-radius:10px;padding:0 19px;background:#fff;color:#6c7b99;box-shadow:0 2px 5px rgba(31,62,108,.03)}
.login-panel .auth-form .login-input input{min-width:0;min-height:68px;flex:1;border:0;padding:0;background:transparent;color:#142a56;font-size:16px;outline:0}.login-input input::placeholder{color:#7e8ba4}.login-input>button{display:grid;width:34px;height:34px;place-items:center;border:0;background:transparent;color:#344e79;cursor:pointer}
.login-panel .auth-form>.primary-button{min-height:66px;margin-top:10px;border:0;border-radius:9px;background:linear-gradient(135deg,#0759e5,#0644c8);box-shadow:0 9px 25px rgba(7,74,210,.18);font-size:19px;font-weight:600}
.login-security{display:flex;align-items:center;justify-content:center;gap:9px;margin-top:58px;color:#67799c;font-size:13px}
.login-footer{position:absolute;right:0;bottom:0;left:0;height:58px;display:grid;place-items:center;border-top:1px solid #e9eef6;background:rgba(255,255,255,.68);color:#657696;font-size:14px}
@media(max-width:1120px){.login-page{grid-template-columns:1fr;padding-bottom:52px;overflow:auto}.login-artwork{min-height:auto;padding-top:20px}.login-introduction{width:min(700px,calc(100% - 40px));padding-bottom:25px}.login-artwork img.login-brand-logo{width:145px;height:125px}.login-introduction h1{font-size:38px}.institution-grid{margin-top:25px}.login-credentials{min-height:auto;padding:25px 20px 45px}.login-panel{min-height:0;padding:48px 55px}.login-footer{height:52px}}
@media(max-width:680px){.login-introduction{width:calc(100% - 28px);padding-top:12px}.login-artwork img.login-brand-logo{width:125px;height:110px}.login-introduction h1{font-size:34px}.login-introduction>p{font-size:14px}.institution-grid{grid-template-columns:repeat(2,1fr);gap:9px}.institution-card{height:105px}.login-credentials{padding-inline:14px}.login-panel{padding:38px 22px;border-radius:18px}.login-panel .login-heading h2{font-size:30px}.login-heading p{margin-bottom:30px;font-size:15px}.login-input{min-height:60px}.login-panel .auth-form .login-input input{min-height:58px}.login-panel .auth-form>.primary-button{min-height:58px}.login-security{margin-top:38px}}
